import React from "react";
import {
  faStore,
  faGift,
  faDatabase,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { APP_BASE_URL, planList } from "../../utils/constant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axiosUserInstance from "../../axiosUserInstance";
import BiomeEnqire from "../../Assets/photo/biome-enqire.png";
import BiomeKeyScan from "../../Assets/photo/biome-key-scan.png";
import BiomeStand from "../../Assets/photo/biome-stand.png";
import PhoneVerificationModal from "../Signup/PhoneVerificationModal";
import { toast } from "react-toastify";

const PlanTab = ({ userData, fetchUserData }) => {
  const [showPhoneModal, setShowPhoneModal] = React.useState(false);
  const [phoneVerified, setPhoneVerified] = React.useState(false);
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [referralCode, setReferralCode] = React.useState("");
  const [isSubmittingReferral, setIsSubmittingReferral] = React.useState(false);
  function getExpiryDate(payment) {
    const purchaseDate = new Date(payment.date);
    let expiryDate;

    if (payment.plan === "monthly") {
      expiryDate = new Date(purchaseDate);
      expiryDate.setMonth(expiryDate.getMonth() + 1);
    } else if (payment.plan === "yearly") {
      expiryDate = new Date(purchaseDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    } else {
      throw new Error("Unknown plan type");
    }

    return expiryDate.toISOString().split("T")[0]; // YYYY-MM-DD format
  }

  const upgradePlan = (type, amount) => {
    createPayment(amount, type);
  };

  const createPayment = async (amount, type) => {
    try {
      const res = await axiosUserInstance.post("/payment/create-payment", {
        amount,
        currency: "USD",
        redirectUrl: `${APP_BASE_URL}/biome/payment-success?plan=${type}`,
      });

      window.location.href = res.data.url; // redirect to payment provider
    } catch (error) {
      console.error("Payment error:", error);
    }
  };

  const handlePhoneVerified = (verifiedPhone) => {
    setPhoneNumber(verifiedPhone);
    setPhoneVerified(true);
    setShowPhoneModal(false);
  };

  const handleReferralFieldFocus = () => {
    if (!phoneVerified) {
      setShowPhoneModal(true);
    }
  };

  const handleRedeemReferral = async () => {
    if (!referralCode || !phoneVerified || !phoneNumber) return;
    setIsSubmittingReferral(true);
    try {
      const res = await axiosUserInstance.post("/referral/redeem", {
        referralCode,
        phone: phoneNumber,
      });
      if (res.status === 200) {
        toast.success(res.data.message);
        setReferralCode("");
        setPhoneVerified(false);
        setPhoneNumber("");
        // If passed from parent, refresh user data to show new plan
        if (fetchUserData) fetchUserData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to redeem code");
    } finally {
      setIsSubmittingReferral(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">
        <FontAwesomeIcon icon={faStore} /> Shop
      </h2>

      {showPhoneModal && (
        <PhoneVerificationModal
          onVerified={handlePhoneVerified}
          onClose={() => setShowPhoneModal(false)}
        />
      )}

      {/* Referral Code Section */}
      <div className="referral-dashboard-section">
        <h3>
          <span>🎁</span> Have a Referral Code?
        </h3>
        {userData?.referredByCode ? (
          <div style={{ color: "green", fontWeight: 500 }}>
            ✓ You have already claimed a referral code:{" "}
            <strong>{userData.referredByCode}</strong>
          </div>
        ) : (
          <div>
            <p className="mb-2" style={{ fontSize: "0.9rem", color: "#666" }}>
              Verify your phone to claim a 1-month Annual Plan trial.
            </p>
            <div className="referral-input-wrapper">
              <input
                type="text"
                className={`form-control referral-input ${phoneVerified ? "referral-unlocked" : "referral-locked"}`}
                placeholder={
                  phoneVerified
                    ? "Enter referral code"
                    : "🔒 Verify phone to unlock"
                }
                value={referralCode}
                onChange={(e) =>
                  setReferralCode(
                    e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")
                  )
                }
                onFocus={handleReferralFieldFocus}
                onClick={handleReferralFieldFocus}
                readOnly={!phoneVerified}
                maxLength={20}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              />
              {phoneVerified && (
                <button
                  type="button"
                  className="btn btn-success mt-3 mb-2"
                  onClick={handleRedeemReferral}
                  disabled={
                    isSubmittingReferral ||
                    !referralCode ||
                    referralCode.length < 6
                  }
                  style={{ whiteSpace: "nowrap" }}
                >
                  {isSubmittingReferral ? "Redeeming..." : "Redeem"}
                </button>
              )}
            </div>
            {phoneVerified && (
              <span
                style={{
                  fontSize: "0.85rem",
                  color: "green",
                }}
              >
                ✓ Phone +{phoneNumber} verified
              </span>
            )}
          </div>
        )}
      </div>

      <div className="plan-cards">
        {planList.map((plan) => {
          const isFree = plan.id === "free";
          const isMonthly = plan.id === "monthly";
          const isYearly = plan.id === "yearly";

          return (
            <div
              key={plan.id}
              className={`plan-card ${
                isYearly
                  ? "popular"
                  : "d-flex flex-column justify-content-between"
              }`}
            >
              {plan.badge && <div className="popular-badge">{plan.badge}</div>}

              <div>
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-price">
                  {plan.priceLabel}{" "}
                  {plan.billingCycle && <small>/ {plan.billingCycle}</small>}
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="plan-feature">
                      <FontAwesomeIcon icon={faCheck} /> {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {isFree && userData?.plan === "Free" && (
                <button className="plan-button btn-outline" disabled>
                  Current Plan
                </button>
              )}

              {isMonthly && (
                <>
                  {userData?.plan === "monthly" ? (
                    <div>
                      {userData?.planDetails && (
                        <p>
                          Next renewal date:{" "}
                          {getExpiryDate(userData?.planDetails)}
                        </p>
                      )}
                      <button className="plan-button btn-outline" disabled>
                        Current Plan
                      </button>
                    </div>
                  ) : userData?.plan !== "yearly" ? (
                    <button
                      className="plan-button btn-primary"
                      onClick={() => upgradePlan("monthly", 6.5)}
                    >
                      Sign me up (Monthly)
                    </button>
                  ) : null}
                </>
              )}

              {isYearly && (
                <>
                  {userData?.plan === "yearly" ? (
                    <div>
                      {userData?.planDetails && (
                        <p>
                          Next renewal date:{" "}
                          {getExpiryDate(userData?.planDetails)}
                        </p>
                      )}
                      <button className="plan-button btn-outline" disabled>
                        Current Plan
                      </button>
                    </div>
                  ) : (
                    <button
                      className="plan-button btn-primary"
                      onClick={() => upgradePlan("yearly", 65)}
                    >
                      Save Big (Yearly)
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="storage-addons">
        <h3 className="section-title">
          <FontAwesomeIcon icon={faDatabase} /> Storage Add-Ons
        </h3>
        <p className="billing-note">(Billed every 3 months)</p>
        <div className="addon-cards">
          {[
            {
              size: "+20MB",
              price: 1.98,
              label: "$1.98",
              desc: "≈ 6 photos or 15s HD video",
            },
            {
              size: "+200MB",
              price: 3.45,
              label: "$3.45",
              desc: "≈ 60 photos or 2min HD video",
            },
            {
              size: "+2GB",
              price: 6.98,
              label: "$6.98",
              desc: "≈ 20min HD or 5min 4K video",
            },
            {
              size: "+10GB",
              price: 9.98,
              label: "$9.98",
              desc: "☁️ Cloud-level storage",
            },
          ].map((a) => (
            <div className="addon-card" key={a.size}>
              <div className="addon-size">{a.size}</div>
              <div className="addon-price">{a.label}</div>
              <div className="addon-desc">{a.desc}</div>
              <button
                className="addon-button"
                onClick={() => upgradePlan(`addon-${a.size}`, a.price)}
              >
                Get Storage
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Gifts */}
      <div className="gift-section">
        <h3 className="section-title">
          <FontAwesomeIcon icon={faGift} /> #BioME.com Merchandise [COMING SOON]
        </h3>
        <div className="gift-cards">
          <div className="gift-card d-flex flex-column justify-content-between">
            <div>
              <img src={BiomeKeyScan} alt="Keychain" className="gift-image" />
              <h4 className="gift-name">Personal Keychain</h4>
              <div className="gift-price">$15</div>
              <ul className="plan-features">
                <li className="plan-feature">
                  <i className="fas fa-check"></i> Official BioME Team
                  Endorsment Badge{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> Iconic #BioME.com Layout{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> Dangling Acrylic
                  Keychain{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> QR code, Name &amp; Bio
                  Embedded{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> Color Customisable (RGB)
                </li>
              </ul>
            </div>
            <button
              className="gift-button"
              onClick={() => alert("Pre-order Keychain (demo)")}
            >
              Pre-order Now
            </button>
          </div>
          <div className="gift-card d-flex flex-column justify-content-between">
            <div>
              <img src={BiomeStand} alt="Signage" className="gift-image" />
              <h4 className="gift-name">Aesthetic Standing Signage</h4>
              <div className="gift-price">$30</div>
              <ul className="plan-features">
                <li className="plan-feature">
                  <i className="fas fa-check"></i> Official BioME Team
                  Endorsment Badge{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> Iconic #BioME.com Layout{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> T-Shaped Acrylic Standee{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> QR code, Name &amp; Bio
                  Embedded{" "}
                </li>
                <li className="plan-feature">
                  <i className="fas fa-check"></i> Color Customisable (RGB)
                </li>
              </ul>
            </div>
            <button
              className="gift-button"
              onClick={() => alert("Pre-order Signage (demo)")}
            >
              Pre-order Now
            </button>
          </div>
          <div className="gift-card d-flex flex-column justify-content-between">
            <div>
              <img src={BiomeEnqire} alt="Enquire" className="gift-image" />
              <h4 className="gift-name">Enquire Below</h4>
              <div className="gift-price">Free Delivery!</div>
              <p>Contact us for custom merchandise options and bulk orders</p>
            </div>
            <button
              className="gift-button"
              onClick={() => alert("Contact for Telegram Chat (demo)")}
            >
              Contact for Telegram Chat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanTab;
